import { useState, type FormEvent } from "react";
import { toast } from "react-hot-toast";

import {
  Button,
  ConfirmDialog,
  FieldRow,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Selector,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TextInput,
  TrashButton,
} from "../components/ui";
import {
  Empty,
  ErrorState,
  Loading,
  PageHeader,
  Panel,
} from "../components/WorkspacePrimitives";
import { copyText } from "../lib/clipboard";
import {
  useApiTokens,
  useCreateApiToken,
  useRevokeApiToken,
  type ApiToken,
  type CreatedApiToken,
} from "./apiTokens";
import SettingsLayout from "./SettingsLayout";
import "./settings.css";

type TokenLifetime = "30" | "90" | "180" | "365";

const TOKEN_LIFETIME_OPTIONS = [
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "180", label: "180 days" },
  { value: "365", label: "365 days" },
] as const satisfies readonly { value: TokenLifetime; label: string }[];

function formatDate(value: string | null): string {
  if (value === null) return "Never";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function TokenRow({ token }: { token: ApiToken }) {
  const revoke = useRevokeApiToken();
  const [confirming, setConfirming] = useState(false);
  const canRevoke = token.status === "active";

  const handleRevoke = () => {
    revoke.mutate(token.id, {
      onSuccess: () => {
        toast.success(`${token.name} was revoked.`);
        setConfirming(false);
      },
      onError: (error) => toast.error(error.message),
    });
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="token-name">
            <strong>{token.name}</strong>
            <small>Ends in {token.token_suffix}</small>
          </div>
        </TableCell>
        <TableCell>
          <span className={`token-status token-status--${token.status}`}>
            {token.status}
          </span>
        </TableCell>
        <TableCell>
          {token.role === "admin" ? "Administrator" : "Launcher"}
        </TableCell>
        <TableCell>{formatDate(token.created_at)}</TableCell>
        <TableCell>{formatDate(token.expires_at)}</TableCell>
        <TableCell className="token-actions">
          {canRevoke && (
            <TrashButton
              label={`Revoke ${token.name} token`}
              onClick={() => setConfirming(true)}
              disabled={revoke.isPending}
            />
          )}
        </TableCell>
      </TableRow>
      {confirming && (
        <TableRow>
          <TableCell colSpan={6}>
            <ConfirmDialog
              title={`Revoke “${token.name}”?`}
              description="This token will stop working immediately and cannot be restored."
              confirmLabel="Revoke token"
              busy={revoke.isPending}
              onCancel={() => setConfirming(false)}
              onConfirm={handleRevoke}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function CreatedTokenNotice({
  created,
  onDismiss,
}: {
  created: CreatedApiToken;
  onDismiss: () => void;
}) {
  const copy = async () => {
    try {
      await copyText(created.token);
      toast.success("Token copied.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Copy failed.");
    }
  };

  return (
    <section className="created-token" aria-labelledby="created-token-title">
      <div>
        <div className="eyebrow">Created successfully</div>
        <h2 id="created-token-title">Save this token now</h2>
        <p>
          This secret is shown once. Paste it into <code>api_key</code> in
          <code> ~/.isara-driver/config.toml</code> before dismissing it.
        </p>
      </div>
      <code>{created.token}</code>
      <div className="created-token__actions">
        <Button tone="primary" onClick={copy}>
          Copy token
        </Button>
        <Button tone="quiet" onClick={onDismiss}>
          I have saved it
        </Button>
      </div>
    </section>
  );
}

export function CreateApiTokenModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (token: CreatedApiToken) => void;
}) {
  const create = useCreateApiToken();
  const [name, setName] = useState("");
  const [lifetime, setLifetime] = useState<TokenLifetime>("365");
  const [noExpiry, setNoExpiry] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedName = name.trim();
    if (normalizedName === "") {
      toast.error("Enter a token name.");
      return;
    }
    try {
      const result = await create.mutateAsync({
        name: normalizedName,
        expires_in_days: noExpiry ? null : Number(lifetime),
      });
      onCreated(result);
      create.reset();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Creation failed.");
    }
  };

  return (
    <Modal size="compact" ariaLabelledBy="create-token-title" onClose={onClose}>
      <ModalHeader
        eyebrow="API tokens"
        title="Create token"
        titleId="create-token-title"
        onClose={onClose}
      />
      <form onSubmit={submit}>
        <ModalBody className="token-create-form">
          <FieldRow label="Token name">
            <TextInput
              aria-label="Token name"
              value={name}
              onValueChange={setName}
              placeholder="My CLI"
              maxLength={100}
              autoComplete="off"
              autoFocus
              required
            />
          </FieldRow>
          <FieldRow label="Lifetime">
            <Selector
              ariaLabel="Lifetime"
              options={TOKEN_LIFETIME_OPTIONS}
              value={lifetime}
              onValueChange={setLifetime}
              disabled={noExpiry}
            />
          </FieldRow>
          <FieldRow label="Expiration">
            <label className="token-no-expiry">
              <input
                type="checkbox"
                checked={noExpiry}
                onChange={(event) => setNoExpiry(event.target.checked)}
              />
              <span>
                No expiration
                <small>Use only for credentials you can revoke promptly.</small>
              </span>
            </label>
          </FieldRow>
        </ModalBody>
        <ModalFooter>
          <Button tone="quiet" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button tone="primary" type="submit" disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create token"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

export default function ApiTokenSettingsPage() {
  const tokens = useApiTokens();
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedApiToken | null>(null);

  return (
    <SettingsLayout>
      <div className="page page--workspace token-settings-page">
        <PageHeader className="settings-page-header">
          <h1>API tokens</h1>
          <Button tone="primary" onClick={() => setCreating(true)}>
            Create token
          </Button>
        </PageHeader>

        {created && (
          <CreatedTokenNotice
            created={created}
            onDismiss={() => setCreated(null)}
          />
        )}

        <Panel
          title="Your tokens"
          meta={tokens.data ? `${tokens.data.length} total` : undefined}
        >
          {tokens.isPending && !tokens.data ? (
            <Loading label="Loading API tokens…" />
          ) : tokens.error && !tokens.data ? (
            <ErrorState error={tokens.error} onRetry={() => tokens.refetch()} />
          ) : !tokens.data || tokens.data.length === 0 ? (
            <Empty>You have not created any API tokens.</Empty>
          ) : (
            <TableContainer>
              <Table className="token-table" aria-label="API tokens">
                <TableHead>
                  <TableRow>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Access</TableHeader>
                    <TableHeader>Created</TableHeader>
                    <TableHeader>Expires</TableHeader>
                    <TableHeader aria-label="Actions" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tokens.data.map((token) => (
                    <TokenRow key={token.id} token={token} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Panel>
        {creating && (
          <CreateApiTokenModal
            onClose={() => setCreating(false)}
            onCreated={setCreated}
          />
        )}
      </div>
    </SettingsLayout>
  );
}
